import { FC, ReactElement, Fragment, useState, useEffect } from 'react';
import { createFragmentContainer, graphql } from 'react-relay/legacy';
import DynamicQuantitySelect from 'dibs-dynamic-quantity-select';

import { PdpSharedQuantitySelect_item$data } from './__generated__/PdpSharedQuantitySelect_item.graphql';

import styles from './PdpSharedQuantitySelect.scss';

type Sku = NonNullable<PdpSharedQuantitySelect_item$data['skus']>[number];
type SellerStatusFields = {
    sellerIsActive: boolean;
    sellerIsComplimentary: boolean;
    sellerIsInternal: boolean;
};
type Props = {
    item: PdpSharedQuantitySelect_item$data;
    children: (ctaQuantity: string | null) => ReactElement;
    selectedSkuId?: string;
    setItemQuantity?: (itemQuantity: number) => void;
};

const INITIAL_QUANTITY = '1';

const getSelectedSku = ({ item, selectedSkuId }: Props): Sku | undefined => {
    const skus = item.skus || [];
    const skuId = selectedSkuId || item.defaultSkuId;
    return skus.find(sku => sku?.serviceId === skuId);
};

const getCtaQuantity = (
    selectedSku: Sku | undefined,
    selectedQuantity: string,
    quantitySelectOptions: boolean
): string | null => {
    if (!quantitySelectOptions) {
        return null;
    }
    const { hasUnlimitedQuantity, quantity } = selectedSku || {};
    const isValidQuantity = typeof quantity === 'number' && Number(selectedQuantity) <= quantity;
    const isValidCtaQuantity = hasUnlimitedQuantity || isValidQuantity;
    return isValidCtaQuantity ? selectedQuantity : INITIAL_QUANTITY;
};

const getSellerStatusFields = (item: Props['item']): SellerStatusFields => {
    const sellerStatus = item.seller?.status;
    const sellerIsActive = sellerStatus === 'active';
    const sellerIsComplimentary = sellerStatus === 'nopay';
    const sellerIsInternal = sellerStatus === 'internal';

    return { sellerIsActive, sellerIsComplimentary, sellerIsInternal };
};

const PdpSharedQuantitySelectComponent: FC<Props> = props => {
    const { children, item, selectedSkuId } = props;
    const [selectedQuantity, setSelectedQuantity] = useState(INITIAL_QUANTITY);
    const [quantitySelectKey, setQuantitySelectKey] = useState(selectedSkuId);

    const selectedSku = getSelectedSku(props);
    const { quantity, quantitySelectOptions, setSize } = selectedSku || {};
    const { isRingItem } = item;

    const ctaQuantity = getCtaQuantity(selectedSku, selectedQuantity, !!quantitySelectOptions);

    // triggered when selectedSkuId changes
    useEffect(() => {
        // reset back to the initial quantity when selectedSkuId changed
        setSelectedQuantity(INITIAL_QUANTITY);
        // re-construct the DynamicQuantitySelect by changing the key
        setQuantitySelectKey(selectedSkuId);
    }, [selectedSkuId]);

    if (quantitySelectOptions) {
        const { isDeleted, isSold, isListingUnlisted } = item;
        const { sellerIsActive, sellerIsComplimentary, sellerIsInternal } =
            getSellerStatusFields(item);
        const showQuantitySelect =
            (sellerIsActive || sellerIsInternal || sellerIsComplimentary) &&
            !isDeleted &&
            !isSold &&
            !isRingItem &&
            !isListingUnlisted;

        return (
            <Fragment>
                <div
                    className={`boxSizingWrapper ${styles.quantitySelect}`}
                    data-tn="quantity-select-wrapper"
                >
                    {showQuantitySelect && (
                        <DynamicQuantitySelect
                            key={quantitySelectKey}
                            availableQuantity={quantity}
                            hasAnimatedPlaceholder
                            onChange={(value: string) => {
                                setSelectedQuantity(value);
                                if (props.setItemQuantity) {
                                    props.setItemQuantity(Number(value));
                                }
                            }}
                            value={selectedQuantity}
                            options={quantitySelectOptions}
                            soldAsSet={setSize && setSize > 1}
                        />
                    )}
                </div>
                {children(ctaQuantity)}
            </Fragment>
        );
    } else {
        return children(ctaQuantity);
    }
};

export const PdpSharedQuantitySelect = createFragmentContainer(PdpSharedQuantitySelectComponent, {
    item: graphql`
        fragment PdpSharedQuantitySelect_item on Item {
            defaultSkuId
            isSold
            isDeleted
            isRingItem
            isListingUnlisted
            seller {
                status
            }
            skus {
                serviceId
                quantity
                hasUnlimitedQuantity
                quantitySelectOptions {
                    label
                    value
                }
                setSize
            }
        }
    `,
});
